import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetWorkflowsDTO from "../../../../models/DTO/IGetWorkflowsDTO";
import IOnGetWorkflowsDTO from "../../../../models/DTO/IOnGetWorkflowsDTO";
import IOnProcessWebhookDTO from "../../../../models/DTO/IOnProcessWebhookDTO";
import IOnRotateWebhookSecretDTO from "../../../../models/DTO/IOnRotateWebhookSecretDTO";
import IProcessWebhookDTO from "../../../../models/DTO/IProcessWebhookDTO";
import IRotateWebhookSecretDTO from "../../../../models/DTO/IRotateWebhookSecretDTO";
import INotification from "../../../../models/INotification";
import IWebhookSummary from "../../../../models/IWebhookSummary";
import IWebhookWorkflowTrigger from "../../../../models/IWebhookWorkflowTrigger";
import IWorkflow from "../../../../models/IWorkflow";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import WebhookEventEnum from "../../../../models/WebhookEventEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { ClickToCopy } from "../../../shared/ClickToCopy/ClickToCopy";
import { FULFILLMENT_STATE_IDS, WORKFLOW_STATE_OPTIONS } from "../WorkflowEditor/workflowSteps";
import { looksLikeWebhookUrl, WEBHOOK_EVENT_GROUPS, webhookEventLabelKey } from "../Webhooks/webhookEvents";
import "./WebhookSidebar.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";

type Props = {
    cancelFn: (refreshData?: boolean) => void;
    /** Omit to create. */
    webhook?: IWebhookSummary;
};

type HeaderRow = { name: string; value: string };

type TriggerState = { order_state_id: OrderStatesEnum; name: string };

/** Every state a workflow may contain, in editor order; the fallback until the business's workflows load. */
const ALL_TRIGGER_STATES: TriggerState[] = [
    ...WORKFLOW_STATE_OPTIONS.map((option) => ({ order_state_id: option.order_state_id, name: option.name })),
    ...Array.from(FULFILLMENT_STATE_IDS).map((id) => ({ order_state_id: id, name: OrderStatesEnum[id].toLowerCase() })),
];

/**
 * Steps a trigger can name: the chosen workflow's own steps, or every step across the business's
 * workflows for "any workflow". Preliminary is never a trigger (orders start there). The full catalog
 * is offered only while the workflows are unavailable (not loaded yet, or the chosen one is gone); a
 * loaded workflow with no eligible steps offers none. A saved step the workflow no longer has stays
 * listed so the select never goes blank.
 */
export const triggerStatesFor = (workflows: IWorkflow[], workflowGuid: string | null, current?: OrderStatesEnum): TriggerState[] => {
    const source = workflowGuid ? workflows.filter((workflow) => workflow.guid === workflowGuid) : workflows;

    if (!workflows.length || !source.length) {
        return ALL_TRIGGER_STATES;
    }

    const seen = new Set<OrderStatesEnum>();
    const states: TriggerState[] = [];

    source.forEach((workflow) =>
        (workflow.detail || []).forEach((step) => {
            const id = Number(step.order_state_id) as OrderStatesEnum;

            if (id === OrderStatesEnum.PRELIMINARY || seen.has(id)) {
                return;
            }

            seen.add(id);
            states.push({ order_state_id: id, name: step.name });
        })
    );

    if (current !== undefined && !seen.has(current)) {
        const known = ALL_TRIGGER_STATES.find((state) => state.order_state_id === current);

        if (known) {
            states.push(known);
        }
    }

    return states;
};

/**
 * Create or edit one webhook endpoint: name, URL, subscriptions (events and workflow triggers),
 * static headers, and on create the signing secret shown once. Editing also offers a secret rotation.
 */
export const WebhookSidebar = (props: Props) => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const editing = !!props.webhook;

    const [name, setName] = useState(props.webhook?.name || "");
    const [url, setUrl] = useState(props.webhook?.url || "");
    const [active, setActive] = useState(props.webhook ? props.webhook.active : true);
    const [events, setEvents] = useState<WebhookEventEnum[]>(props.webhook?.events || []);
    const [triggers, setTriggers] = useState<IWebhookWorkflowTrigger[]>(props.webhook?.workflow_triggers || []);
    const [replaceHeaders, setReplaceHeaders] = useState(!editing || !(props.webhook?.header_names || []).length);
    const [headers, setHeaders] = useState<HeaderRow[]>([]);
    const [workflows, setWorkflows] = useState<IWorkflow[]>([]);
    const [saving, setSaving] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [confirmRotate, setConfirmRotate] = useState(false);
    const [secret, setSecret] = useState<string | null>(null);
    const [errors, setErrors] = useState<string[]>([]);

    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    const base = () => ({ userGuid: userModel.guid, userToken: userModel.token, supplierGuid: supplierModel.guid });

    const onProcessWebhook = (data: IOnProcessWebhookDTO) => {
        setSaving(false);

        const notification: INotification = {
            message: "successfully_processed_webhook",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        if (data.signing_secret) {
            setSecret(data.signing_secret);
        } else {
            props.cancelFn(true);
        }
    };

    const onRotateSecret = (data: IOnRotateWebhookSecretDTO) => {
        setRotating(false);
        setConfirmRotate(false);

        const notification: INotification = {
            message: "successfully_rotated_webhook_secret",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);
        setSecret(data.signing_secret);
    };

    const onGetWorkflows = (data: IOnGetWorkflowsDTO) => {
        setWorkflows(data.workflows || []);
    };

    /**
     * The API answers a rejected save or rotation with a notify error (a WEBHOOK_* code, or the action's
     * generic ERROR_ code); the toast layer shows it. Drop the pending state so the buttons work again.
     * Other errors are left alone so an unrelated toast cannot re-enable a request still in flight.
     */
    const onNotify = (payload: { type?: string; message?: string }) => {
        if (
            payload?.type !== "error" ||
            !/^(WEBHOOK_|ERROR_(PROCESS_WEBHOOK|ROTATE_WEBHOOK_SECRET)$)/.test(String(payload.message || ""))
        ) {
            return;
        }

        setSaving(false);
        setRotating(false);
        setConfirmRotate(false);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.WebhooksOnProcessWebhook, onProcessWebhook);
        socket.on(EndpointsEnum.WebhooksOnRotateSecret, onRotateSecret);
        socket.on(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
        socket.on(EndpointsEnum.Notify, onNotify);

        const payload: IGetWorkflowsDTO = base();

        socket.emit(EndpointsEnum.WorkflowsGetWorkflows, payload);

        return () => {
            socket.off(EndpointsEnum.WebhooksOnProcessWebhook, onProcessWebhook);
            socket.off(EndpointsEnum.WebhooksOnRotateSecret, onRotateSecret);
            socket.off(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
            socket.off(EndpointsEnum.Notify, onNotify);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleEvent = (event: WebhookEventEnum) => (e: ChangeEvent<HTMLInputElement>) => {
        setEvents((previous) =>
            e.target.checked ? Array.from(new Set([...previous, event])) : previous.filter((candidate) => candidate !== event)
        );
    };

    const toggleGroup = (groupEvents: WebhookEventEnum[], on: boolean) => {
        setEvents((previous) =>
            on ? Array.from(new Set([...previous, ...groupEvents])) : previous.filter((candidate) => !groupEvents.includes(candidate))
        );
    };

    const updateTrigger = (index: number, patch: Partial<IWebhookWorkflowTrigger>) => {
        setTriggers((previous) => previous.map((trigger, i) => (i === index ? { ...trigger, ...patch } : trigger)));
    };

    /** Pick the workflow (or any), then land on its first step when the current one is not in it. */
    const setTriggerWorkflow = (index: number, workflowGuid: string | null) => {
        setTriggers((previous) =>
            previous.map((trigger, i) => {
                if (i !== index) {
                    return trigger;
                }

                const states = triggerStatesFor(workflows, workflowGuid);
                const keep = !states.length || states.some((state) => state.order_state_id === Number(trigger.order_state_id));

                return { workflow_guid: workflowGuid, order_state_id: keep ? trigger.order_state_id : states[0].order_state_id };
            })
        );
    };

    const addTrigger = () => {
        const states = triggerStatesFor(workflows, null);
        const first = states.find((state) => state.order_state_id === OrderStatesEnum.SUBMITTED) || states[0];

        setTriggers((previous) => [
            ...previous,
            { workflow_guid: null, order_state_id: first?.order_state_id ?? OrderStatesEnum.SUBMITTED },
        ]);
    };

    const removeTrigger = (index: number) => {
        setTriggers((previous) => previous.filter((_, i) => i !== index));
    };

    const updateHeader = (index: number, patch: Partial<HeaderRow>) => {
        setHeaders((previous) => previous.map((header, i) => (i === index ? { ...header, ...patch } : header)));
    };

    const validate = (): string[] => {
        const problems: string[] = [];

        if (!name.trim()) {
            problems.push("webhook_name_required");
        }

        if (!looksLikeWebhookUrl(url)) {
            problems.push(url.trim().toLowerCase().startsWith("http://") ? "webhook_url_scheme" : "webhook_url_syntax");
        }

        if (!events.length && !triggers.length) {
            problems.push("webhook_subscription_required");
        }

        return problems;
    };

    const save = () => {
        const problems = validate();

        setErrors(problems);

        if (problems.length) {
            return;
        }

        const payload: IProcessWebhookDTO = {
            ...base(),
            name: name.trim(),
            url: url.trim(),
            active,
            events,
            workflow_triggers: triggers.map((trigger) => ({
                workflow_guid: trigger.workflow_guid || null,
                order_state_id: Number(trigger.order_state_id),
            })),
        };

        if (editing) {
            payload.guid = props.webhook!.guid;
        }

        if (replaceHeaders) {
            payload.headers = {};

            for (const header of headers) {
                if (header.name.trim() && header.value.trim()) {
                    payload.headers[header.name.trim()] = header.value.trim();
                }
            }
        }

        setSaving(true);
        socket.emit(EndpointsEnum.WebhooksProcessWebhook, payload);
    };

    const rotate = () => {
        if (!confirmRotate) {
            setConfirmRotate(true);

            return;
        }

        const payload: IRotateWebhookSecretDTO = { ...base(), guid: props.webhook!.guid };

        setRotating(true);
        socket.emit(EndpointsEnum.WebhooksRotateSecret, payload);
    };

    const usedTriggerKeys = useMemo(
        () => new Set(triggers.map((trigger) => `${trigger.workflow_guid || "*"}:${trigger.order_state_id}`)),
        [triggers]
    );

    const header = (
        <div className={"col-12"}>
            <div className="text-end">
                <button className="btn btn-danger btn-sm" onClick={() => props.cancelFn(!!secret)} data-testid={"webhook-sidebar-close"}>
                    <i className="far fa-times" />
                </button>
            </div>
            <OwTitle className={"mb-2"} leftIconName={"fad fa-bolt"} title={"webhook_endpoint"} />
            <hr />
        </div>
    );

    if (secret) {
        return (
            <div className={"WebhookSidebar p-2"} data-testid={"webhook-sidebar"}>
                {header}
                <div className={"row"}>
                    <div className={"col-12"}>
                        <OwLabel title={"name"} isBold={true} postText={":"} className={"me-2"} />
                        {name}
                    </div>
                    <div className={"col-12 d-flex text-warning mt-2"}>
                        <i className={"fad fa-exclamation-triangle me-2"} />
                        <OwLabel title={"webhook_secret_help_warning"} />
                    </div>
                    <div className={"col-12"}>
                        <OwLabel title={"signing_secret"} isBold={true} postText={":"} className={"me-2"} />
                        <div className={"d-inline-flex"} data-testid={"webhook-secret"}>
                            <ClickToCopy text={secret} />
                        </div>
                    </div>
                    <div className={"col-12 mt-3"}>
                        <OwButton title={"close"} fn={() => props.cancelFn(true)} testId={"webhook-secret-done"} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={"WebhookSidebar p-2"} data-testid={"webhook-sidebar"}>
            {header}

            {errors.length ? (
                <div className={"WebhookSidebar-errors"} role={"alert"} data-testid={"webhook-errors"}>
                    {errors.map((error) => (
                        <div key={error}>
                            <i className={"fas fa-exclamation-circle me-1"} aria-hidden={true} />
                            {t(error)}
                        </div>
                    ))}
                </div>
            ) : null}

            <div className={"row"}>
                <div className={"col-12"}>
                    <OwLabel title={"name"} />
                    <OwInputWrapper
                        name={"name"}
                        fn={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        value={name}
                        maxLength={255}
                    />
                </div>
                <div className={"col-12 mt-2"}>
                    <OwLabel title={"endpoint_url"} />
                    <OwInputWrapper name={"url"} fn={(e: ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)} value={url} />
                    <div className={"WebhookSidebar-help"}>{t("help_text_endpoint_url")}</div>
                </div>
                {editing ? (
                    <div className={"col-12 mt-2 WebhookSidebar-toggle"}>
                        <OwToggle
                            size={"small"}
                            name={"active"}
                            label={"webhook_active"}
                            active={active}
                            fn={(e: any) => setActive(!!e.target.value)}
                        />
                        <div className={"WebhookSidebar-help"}>{t("help_text_webhook_active")}</div>
                    </div>
                ) : null}
            </div>

            <div className={"WebhookSidebar-section"} data-testid={"webhook-events"}>
                <OwLabel title={"subscribed_events"} isBold={true} noBottomMargin={true} />
                <div className={"WebhookSidebar-help"}>{t("help_text_subscribed_events")}</div>
                {WEBHOOK_EVENT_GROUPS.map((group) => {
                    const allOn = group.events.every((event) => events.includes(event));

                    return (
                        <div className={"WebhookSidebar-group"} key={group.labelKey}>
                            <button
                                type={"button"}
                                className={"WebhookSidebar-group-title"}
                                onClick={() => toggleGroup(group.events, !allOn)}
                                data-testid={`webhook-group-${group.labelKey}`}
                            >
                                <OwLabel
                                    title={group.labelKey}
                                    noBottomMargin={true}
                                    className={"pointer"}
                                    isSupplierVendorOverride={!!group.vendorWord}
                                    supplierModel={supplierModel}
                                />
                            </button>
                            {group.events.map((event) => (
                                <OwCheckbox
                                    key={event}
                                    name={event}
                                    label={webhookEventLabelKey(event)}
                                    checked={events.includes(event)}
                                    fn={toggleEvent(event)}
                                    className={"WebhookSidebar-event"}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>

            <div className={"WebhookSidebar-section"} data-testid={"webhook-triggers"}>
                <OwLabel title={"workflow_triggers"} isBold={true} noBottomMargin={true} />
                <div className={"WebhookSidebar-help"}>{t("help_text_workflow_triggers")}</div>
                {!triggers.length ? <div className={"WebhookSidebar-muted"}>{t("no_workflow_triggers")}</div> : null}
                {triggers.map((trigger, index) => (
                    <div className={"WebhookSidebar-trigger"} key={index} data-testid={`webhook-trigger-${index}`}>
                        <select
                            className={"form-select form-select-sm"}
                            value={trigger.workflow_guid || ""}
                            aria-label={t("workflow")}
                            onChange={(e) => setTriggerWorkflow(index, e.target.value || null)}
                        >
                            <option value={""}>{t("any_workflow")}</option>
                            {workflows.map((workflow) => (
                                <option key={workflow.guid} value={workflow.guid}>
                                    {workflow.name}
                                </option>
                            ))}
                        </select>
                        <select
                            className={"form-select form-select-sm"}
                            value={trigger.order_state_id}
                            aria-label={t("step")}
                            onChange={(e) => updateTrigger(index, { order_state_id: Number(e.target.value) as OrderStatesEnum })}
                        >
                            {triggerStatesFor(
                                workflows,
                                trigger.workflow_guid || null,
                                Number(trigger.order_state_id) as OrderStatesEnum
                            ).map((state) => (
                                <option key={state.order_state_id} value={state.order_state_id}>
                                    {t(state.name)}
                                </option>
                            ))}
                        </select>
                        <button
                            type={"button"}
                            className={"btn btn-link text-muted btn-sm"}
                            aria-label={`${t("remove")} ${index + 1}`}
                            onClick={() => removeTrigger(index)}
                        >
                            <i className={"fad fa-xmark"} aria-hidden={true} />
                        </button>
                    </div>
                ))}
                {usedTriggerKeys.size !== triggers.length ? (
                    <div className={"WebhookSidebar-muted"}>{t("webhook_workflow_triggers_invalid")}</div>
                ) : null}
                <OwButton
                    title={"add_trigger"}
                    className={"btn-link btn-sm ps-0"}
                    leftIconClass={"fas fa-plus"}
                    fn={addTrigger}
                    disableWidthOnClick={true}
                    disableDblClickPrevention={true}
                    testId={"webhook-add-trigger"}
                />
            </div>

            <div className={"WebhookSidebar-section"} data-testid={"webhook-headers"}>
                <OwLabel title={"static_headers"} isBold={true} noBottomMargin={true} />
                <div className={"WebhookSidebar-help"}>{t("help_text_static_headers")}</div>
                {editing && !replaceHeaders ? (
                    <div className={"WebhookSidebar-saved-headers"}>
                        {(props.webhook?.header_names || []).map((headerName) => (
                            <span className={"SettingsChip on"} key={headerName}>
                                <i className={"fad fa-lock"} aria-hidden={true} />
                                {headerName}
                                <span className={"WebhookSidebar-muted ms-1"}>({t("header_value_hidden")})</span>
                            </span>
                        ))}
                        <OwButton
                            title={"edit"}
                            className={"btn-link btn-sm ps-0"}
                            leftIconClass={"fad fa-pen-to-square"}
                            fn={() => setReplaceHeaders(true)}
                            disableWidthOnClick={true}
                            disableDblClickPrevention={true}
                            testId={"webhook-replace-headers"}
                        />
                    </div>
                ) : (
                    <>
                        {headers.map((row, index) => (
                            <div className={"WebhookSidebar-header"} key={index} data-testid={`webhook-header-${index}`}>
                                <input
                                    className={"form-control form-control-sm"}
                                    placeholder={t("header_name")}
                                    aria-label={t("header_name")}
                                    value={row.name}
                                    onChange={(e) => updateHeader(index, { name: e.target.value })}
                                />
                                <input
                                    className={"form-control form-control-sm"}
                                    placeholder={t("header_value")}
                                    aria-label={t("header_value")}
                                    value={row.value}
                                    onChange={(e) => updateHeader(index, { value: e.target.value })}
                                />
                                <button
                                    type={"button"}
                                    className={"btn btn-link text-muted btn-sm"}
                                    aria-label={`${t("remove")} ${row.name || index + 1}`}
                                    onClick={() => setHeaders((previous) => previous.filter((_, i) => i !== index))}
                                >
                                    <i className={"fad fa-xmark"} aria-hidden={true} />
                                </button>
                            </div>
                        ))}
                        {headers.length < 10 ? (
                            <OwButton
                                title={"add_header"}
                                className={"btn-link btn-sm ps-0"}
                                leftIconClass={"fas fa-plus"}
                                fn={() => setHeaders((previous) => [...previous, { name: "", value: "" }])}
                                disableWidthOnClick={true}
                                disableDblClickPrevention={true}
                                testId={"webhook-add-header"}
                            />
                        ) : null}
                    </>
                )}
            </div>

            {editing ? (
                <div className={"WebhookSidebar-section"} data-testid={"webhook-rotate"}>
                    <OwLabel title={"signing_secret"} isBold={true} noBottomMargin={true} />
                    <div className={"WebhookSidebar-help"}>{t("help_text_rotate_secret")}</div>
                    <div className={"d-flex gap-2 align-items-center"}>
                        <OwButton
                            title={confirmRotate ? "confirm_rotate_secret" : "rotate_secret"}
                            className={confirmRotate ? "btn-warning btn-sm" : "btn-outline-secondary btn-sm"}
                            leftIconClass={"fad fa-rotate"}
                            fn={rotate}
                            active={rotating}
                            disableOnActive={true}
                            disableWidthOnClick={true}
                            disableDblClickPrevention={true}
                            testId={"webhook-rotate-secret"}
                        />
                        {confirmRotate ? (
                            <OwButton
                                title={"cancel"}
                                className={"btn-link text-muted btn-sm"}
                                fn={() => setConfirmRotate(false)}
                                disableWidthOnClick={true}
                                disableDblClickPrevention={true}
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}

            <div className={"row mt-3"}>
                <div className={"col-12"}>
                    <OwButton
                        title={editing ? "update" : "create"}
                        fn={save}
                        active={saving}
                        disableOnActive={true}
                        testId={"webhook-save"}
                    />
                </div>
            </div>
        </div>
    );
};
